import { UnifiedPageLayout } from '@knitknot/shared/components/ui/unified-page-layout';
import { SectionHeader } from '@knitknot/shared/components/ui/section-header';
import { Skeleton } from '@knitknot/shared/components/ui/skeleton';
import { ChartSkeleton } from '@knitknot/shared/components/ui/chart-skeleton';

/** One shell across the onboarding check, route import, and coverage read.
 *
 *  It MIRRORS `OverviewPage`'s bands, in order and in geometry, because a
 *  skeleton that does not is worse than none: it promises a layout the load
 *  then rearranges. This one carried a full-width "Benchmark trend" band for a
 *  card that was deleted when the two competitive charts landed, so every cold
 *  load drew a 260px chart and then dropped it (rule 59 — a skeleton mirrors
 *  its destination; rule 67 — nothing below it may move).
 *
 *  The two competitive bands are `3fr / 2fr` like the real ones, so the chart
 *  ghost and the list ghost sit on the same split the content lands on.
 *
 *  Moved with the 2026-09-18 layout: "Latest benchmark" lost its plot (it is
 *  news sentences and a `→` now), "Top cited pages" took a full-width band of
 *  its own, and the AI Presence chart landed beside Playbooks — so the last
 *  band is a `grid-cols-2` with a CHART ghost on the left and a list ghost on
 *  the right, not two list ghosts.
 */
export function OverviewSkeleton() {
  return (
    <UnifiedPageLayout
      title="Overview"
      description="Weekly performance, verified agent reach, and the work that needs attention next."
    >
      <div className="space-y-4" aria-busy="true" aria-label="Loading Overview">
        <section className="border-y border-border bg-card p-4 sm:p-5">
          <SectionHeader title="Latest benchmark" />
          <div className="mt-2">
            <Skeleton className="h-5 w-3/4" />
          </div>
        </section>
        {[
          ['Visibility over time', 'Visibility ranking'],
          ['Head-to-head over time', 'Head-to-head performance'],
        ].map(([chartTitle, listTitle]) => (
          <div
            key={chartTitle}
            className="grid border-y border-border bg-card lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]"
          >
            <section className="min-w-0 p-4 sm:p-5 lg:border-r lg:border-border">
              <SectionHeader title={chartTitle} />
              <div className="mt-2">
                <ChartSkeleton height={260} className="border-none bg-transparent p-0" />
              </div>
            </section>
            <section className="min-w-0 border-t border-border p-4 sm:p-5 lg:border-t-0">
              <SectionHeader title={listTitle} />
              <div className="mt-2">
                <Skeleton className="h-40" />
              </div>
            </section>
          </div>
        ))}
        <section className="border-y border-border bg-card p-4 sm:p-5">
          <SectionHeader title="Top cited pages" />
          <div className="mt-2">
            <Skeleton className="h-40" />
          </div>
        </section>
        <div className="grid border-y border-border bg-card lg:grid-cols-2">
          <section className="min-w-0 p-4 sm:p-5 lg:border-r lg:border-border">
            <SectionHeader title="AI Presence" />
            <div className="mt-2">
              {/* The loaded chart is `HomeScoreTrend` in `readout` mode: a
                  ~26px metric/legend row over a 132px plot band plus its axis
                  strips — not the 260px the competitive charts get. */}
              <ChartSkeleton height={208} className="border-none bg-transparent p-0" />
            </div>
          </section>
          <section className="min-w-0 border-t border-border p-4 sm:p-5 lg:border-t-0">
            <SectionHeader title="Playbooks" />
            <div className="mt-2">
              <Skeleton className="h-40" />
            </div>
          </section>
        </div>
      </div>
    </UnifiedPageLayout>
  );
}
